import Flex from '@deprecated-ui/common/Flex';
import Avatar from '@deprecated-ui/common/Avatar';
import color from '@deprecated-ui/_style/color';
import { LabelAdmin, LabelVerifiedAccount } from 'global/Labels';
import Link from 'next/link';
import moment from 'moment';
import Image from 'global/Image';
import { AVATAR_URL } from 'constants/api';
import { handleErrorAvatar } from 'utils/general';
import { isClient } from 'constants/app';
import {
  mainProfileEvents,
  PAGE_SOURCE,
  PAGE_SOURCE_INDEX,
} from 'features/user/analytics/ProfileAnalytics';
import { useState } from 'react';
import alertMessage from 'global/AlertMessage';
import SUCCESS_MESSAGE from 'constants/successMessage';
import If from 'global/ErrorHandlers/If';
import { PopoverUser } from '../Popover';
import StreamItemOption from './StreamItemOption';
import {
  HeaderContent,
  TextHeader,
  BadgeJunkWrapper,
  BadgeJunk,
} from './styled';
import { StreamPostData } from '../../../../@types/stream';
import { StreamEntryPoint } from '../types';
import { ModalReport } from '../Modals';
import WarningBadge from './WarningBadge';

interface HeaderProps {
  post: StreamPostData;
  avatarSize?: string;
  isLoggedIn?: boolean;
  withMenu?: boolean;
  entryPoint?: StreamEntryPoint;
  symbol?: string;
}

function Header({
  post,
  avatarSize = 'medium',
  isLoggedIn = false,
  withMenu = true,
  symbol = '',
  entryPoint,
}: HeaderProps) {
  const [modalReport, showModalReport] = useState<boolean>(false);
  const [modalWarning, setModalWarning] = useState(false);
  const {
    avatar,
    created_display: created,
    username,
    official,
    saved,
    follow,
    postid,
    userid,
    user_priv: privilege,
    updated,
    polling,
    isJunk,
    isSpam,
    is_violation,
  } = post;
  const avatarUrl = avatar?.startsWith('http')
    ? avatar
    : `${AVATAR_URL}/${avatar}`;
  const isFollowed: boolean = follow === 1;

  // eslint-disable-next-line use-isnan
  const hasEdited: boolean = updated !== '0000-00-00 00:00:00';
  const isPolling: boolean = polling.length > 0;

  const getPageSourceIndex = () => {
    if (!isClient()) {
      return '';
    }

    const { href } = window.location;
    if (href.includes('/symbol/')) {
      return PAGE_SOURCE_INDEX.findIndex(
        (sourceIndex) => sourceIndex === 'Company Page',
      );
    }
    if (href.includes('/stream')) {
      return PAGE_SOURCE_INDEX.findIndex(
        (sourceIndex) => sourceIndex === 'Stream Post',
      );
    }
    return '';
  };

  const viewProfileAnalytics = () => {
    const { href } = window.location;
    let pageSource: PAGE_SOURCE;

    if (href.includes('/symbol/')) {
      pageSource = PAGE_SOURCE.companyPage;
    } else if (href.includes('/stream')) {
      pageSource = PAGE_SOURCE.streamPost;
    }
    if (pageSource) {
      mainProfileEvents.onViewProfile(pageSource);
    }
  };

  const handleReportPost = async (val) => {
    const data = {
      postid,
      ...val,
    };
    // @ts-ignore
    const report = await dispatch(effects.postReportStream(data));

    // @ts-ignore
    const { error } = report?.payload ?? {};

    if (!error) {
      showModalReport(false);
      setModalWarning(false);
      alertMessage({
        content: SUCCESS_MESSAGE.REPORT_DELIVERED,
        alertType: 'snackbar',
        messageType: 'success',
        hasCloseIcon: false,
      });
    }
  };

  return (
    <Flex width="100%" className="header">
      {isLoggedIn ? (
        <PopoverUser username={username}>
          <span>
            <Link href={`/${username}?source=${getPageSourceIndex()}`} passHref>
              <a
                href="dummy"
                onClick={viewProfileAnalytics}
                style={{ color: 'unset' }}
              >
                <Avatar size={avatarSize} padding="0">
                  <Image
                    alt="imageProfile"
                    src={avatarUrl}
                    onError={(e) => handleErrorAvatar(e, username)}
                    style={{ cursor: 'pointer' }}
                  />
                </Avatar>
              </a>
            </Link>
          </span>
        </PopoverUser>
      ) : (
        <Link href={`/${username}?source=${getPageSourceIndex()}`} passHref>
          <a
            href="dummy"
            onClick={viewProfileAnalytics}
            style={{ color: 'unset' }}
          >
            <Avatar size={avatarSize} padding="0">
              <Image
                alt="imageProfile"
                src={avatarUrl}
                onError={(e) => handleErrorAvatar(e, username)}
                style={{ cursor: 'pointer' }}
              />
            </Avatar>
          </a>
        </Link>
      )}
      <HeaderContent>
        <div>
          <Flex align="center">
            {isLoggedIn ? (
              <PopoverUser username={username}>
                <span className={`popover-trigger-${username}`}>
                  <Link
                    href={`/${username}?source=${getPageSourceIndex()}`}
                    passHref
                  >
                    <TextHeader
                      onClick={viewProfileAnalytics}
                      size="16px"
                      family="bold"
                    >
                      {username}
                    </TextHeader>
                  </Link>
                </span>
              </PopoverUser>
            ) : (
              <Link
                href={`/${username}?source=${getPageSourceIndex()}`}
                passHref
              >
                <TextHeader
                  onClick={viewProfileAnalytics}
                  size="16px"
                  family="bold"
                >
                  {username}
                </TextHeader>
              </Link>
            )}
            <Flex align="center">
              <LabelVerifiedAccount
                deprecatedVerifiedStatus={official > 0}
                verifiedStatus={post?.verified_status}
              />
              <LabelAdmin privilege={privilege} />
            </Flex>
          </Flex>
          <Link href={`/post/${postid}`}>
            <a style={{ color: 'unset' }} target="_blank">
              <TextHeader size="12px" color={color.midgray}>
                {created}
                {hasEdited
                  ? ` Edited on ${moment(updated).format('D MMM YY, HH:mm')}`
                  : null}
              </TextHeader>
            </a>
          </Link>
        </div>
        <BadgeJunkWrapper style={{ position: 'absolute', right: 25 }}>
          <BadgeJunk type="junk" visible={isJunk}>
            Potential Junk
          </BadgeJunk>
          <BadgeJunk type="spam" visible={isSpam}>
            Potential Spam
          </BadgeJunk>
          <WarningBadge
            visible={!withMenu && is_violation}
            modalReport={modalReport}
            showModalReport={showModalReport}
            modalWarning={modalWarning}
            setModalWarning={setModalWarning}
          />
        </BadgeJunkWrapper>

        {withMenu && (
          <StreamItemOption
            userId={userid}
            postId={postid}
            post={post}
            saved={saved}
            followed={isFollowed}
            showFollowNotification={false}
            isPolling={isPolling}
            type="post"
            entryPoint={entryPoint}
            symbol={symbol}
            isViolation={is_violation}
          />
        )}

        <If condition={!withMenu && is_violation}>
          <ModalReport
            visible={modalReport}
            destroyOnClose
            onReport={handleReportPost}
            onCancel={() => showModalReport(false)}
          />
        </If>
      </HeaderContent>
    </Flex>
  );
}

export default Header;
